import NoSSR from "@mpth/react-no-ssr";
import React, { useEffect } from "react";
import { AccessCheck } from "@components/AuthCheck";
import { Lesson } from "@components/Lesson";
import { PageProps, graphql } from "gatsby";
import { signupTracer } from "@apollo/signup-tracer";
import { utmGrabber } from "@apollo/utm-grabber";

export default function LessonTemplate({
  data,
  pageContext,
}: PageProps<
  Queries.GetLessonQuery,
  {
    lessons: Queries.LessonFragment[];
  }
>) {
  useEffect(() => {
    signupTracer("odyssey");
    utmGrabber();
  }, []);
  if (!data.lesson) {
    throw new Error("Lesson not found");
  }

  const { frontmatter, body, slug, timeToRead, course } = data.lesson;

  if (!course) {
    throw new Error("Lesson has no course");
  }

  const { memberships, tiers } = course;

  const lesson = (
    <Lesson
      lessons={pageContext.lessons}
      course={course}
      frontmatter={frontmatter!}
      body={body}
      slug={slug}
      timeToRead={timeToRead}
    />
  );

  const isPartnerCourse = course.slug.startsWith("partners/");

  if (
    !isPartnerCourse &&
    !memberships?.length &&
    !tiers?.length &&
    course.status !== "Coming soon"
  ) {
    return lesson;
  }

  // otherwise...
  return (
    // don't statically render the page to avoid flashing the login form
    <NoSSR>
      <AccessCheck
        memberships={memberships}
        tiers={tiers}
        isPartnerCourse={isPartnerCourse}
        courseStatus={course.status}
      >
        {lesson}
      </AccessCheck>
    </NoSSR>
  );
}

export const query = graphql`
  query GetLesson($id: String) {
    lesson: mdx(id: { eq: $id }) {
      body
      slug
      timeToRead
      frontmatter {
        title
        videoId
        time
        description
        seo {
          title
          description
        }
      }
      course {
        id
        title
        slug
        memberships
        unlisted
        tiers
        showEnterprisePlanCallout
        ghFeedbackEnabled
        marketoProgramId
        marketoLeadSourceDetail
        educationLevel
        status
        version
        progressId
        changelog
        thumb {
          src
          alt
        }
        olderVersion {
          id
          slug
          title
          progressId
          version
          lessons {
            id
            slug
            tasks {
              id
            }
          }
        }
        languages
      }
    }
  }
`;
